import { cs } from '../../../lib/currency';
import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import {
  Plus, Pencil, Trash2, Settings, Download,
  ArrowUp, ArrowDown, ArrowUpDown, X, Search,
} from 'lucide-react';

// iter331 — Tournament expenses: full filter bar, custom-category-aware
// dropdown, sortable columns, and CSV export of the currently-filtered
// rows. All filter state is local to this tab so the parent
// TournamentDetail component stays focused on wiring/data.

const ALL = '__all__';

const FILTER_DEFAULTS = {
  category: ALL,
  age: ALL,
  team: ALL,
  dateFrom: '',
  dateTo: '',
  amountMin: '',
  amountMax: '',
  search: '',
};

const SORT_KEYS = {
  expense_date: (e) => e.expense_date || '',
  category: (e) => (e.category || '').toLowerCase(),
  age_category: (e) => (e.age_category || '').toLowerCase(),
  team: (e, ctx) => {
    if (!e.team_id) return '';
    const t = (ctx.teams || []).find((x) => x.id === e.team_id);
    return (t ? t.name : '').toLowerCase();
  },
  description: (e) => (e.description || '').toLowerCase(),
  amount: (e) => e.amount ?? 0,
};

const displayCategoryName = (code, categories) => {
  const match = (categories || []).find((c) => c.code === code || c.id === code);
  if (match) return match.name;
  return (code || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const SortableHeader = ({ label, sortKey, sort, setSort, className, testId }) => {
  const active = sort.key === sortKey;
  const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  const toggle = () => {
    if (!active) return setSort({ key: sortKey, dir: 'asc' });
    if (sort.dir === 'asc') return setSort({ key: sortKey, dir: 'desc' });
    return setSort({ key: null, dir: 'asc' }); // reset to natural order
  };
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={toggle}
        className={`inline-flex items-center gap-1 select-none hover:text-blue-600 transition-colors ${active ? 'text-blue-700 font-semibold' : ''}`}
        data-testid={testId}
      >
        {label}
        <Icon className="h-3 w-3 opacity-70" />
      </button>
    </TableHead>
  );
};

export const ExpensesTab = ({
  tournament,
  teams,
  expenses,
  expenseCategories,
  onAddExpense,
  onEditExpense,
  onDeleteExpense,
  onManageCategories,
  isAdmin,
}) => {
  const [filters, setFilters] = useState(FILTER_DEFAULTS);
  // iter331b — start with no active sort key so the tri-state
  // (asc → desc → reset) contract works on the first click of ANY
  // column, including Date. Default row order (date-desc) is applied
  // as a fallback inside the sort memo, so the initial view is
  // still date-descending but no arrow is highlighted.
  const [sort, setSort] = useState({ key: null, dir: 'asc' });

  const setFilter = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const clearFilters = () => setFilters(FILTER_DEFAULTS);

  const activeCategories = useMemo(
    () => (expenseCategories || []).filter((c) => c.is_active !== false),
    [expenseCategories],
  );

  const filtered = useMemo(() => {
    const { category, age, team, dateFrom, dateTo, amountMin, amountMax, search } = filters;
    const min = amountMin === '' ? null : Number(amountMin);
    const max = amountMax === '' ? null : Number(amountMax);
    const q = (search || '').trim().toLowerCase();

    return (expenses || []).filter((e) => {
      if (category !== ALL && e.category !== category) return false;
      if (age !== ALL) {
        if (age === 'general' && e.age_category) return false;
        if (age !== 'general' && e.age_category !== age) return false;
      }
      if (team !== ALL) {
        if (team === '__none__' && e.team_id) return false;
        if (team !== '__none__' && e.team_id !== team) return false;
      }
      if (dateFrom && (!e.expense_date || e.expense_date < dateFrom)) return false;
      if (dateTo && (!e.expense_date || e.expense_date > dateTo)) return false;
      if (min !== null && !Number.isNaN(min) && (e.amount ?? 0) < min) return false;
      if (max !== null && !Number.isNaN(max) && (e.amount ?? 0) > max) return false;
      if (q) {
        const hay = `${e.description || ''} ${displayCategoryName(e.category, expenseCategories)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [expenses, filters, expenseCategories]);

  const sorted = useMemo(() => {
    if (!sort.key) {
      // iter331b — Default view: expense_date descending. This keeps
      // the initial-render behaviour identical to the prior UX while
      // letting SortableHeader own the tri-state cycle cleanly.
      return [...filtered].sort((a, b) => (b.expense_date || '').localeCompare(a.expense_date || ''));
    }
    const getter = SORT_KEYS[sort.key];
    if (!getter) return filtered;
    const ctx = { teams };
    const dirMul = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = getter(a, ctx);
      const bv = getter(b, ctx);
      if (av < bv) return -1 * dirMul;
      if (av > bv) return 1 * dirMul;
      return 0;
    });
  }, [filtered, sort, teams]);

  const total = useMemo(
    () => sorted.reduce((sum, e) => sum + (e.amount || 0), 0),
    [sorted],
  );

  // ------------- Active filter chips -------------
  const chips = [];
  if (filters.category !== ALL) {
    chips.push({
      key: 'category',
      label: `Category: ${displayCategoryName(filters.category, expenseCategories)}`,
      clear: () => setFilter({ category: ALL }),
    });
  }
  if (filters.age !== ALL) {
    chips.push({
      key: 'age',
      label: `Age: ${filters.age === 'general' ? 'General' : filters.age.toUpperCase()}`,
      clear: () => setFilter({ age: ALL }),
    });
  }
  if (filters.team !== ALL) {
    const teamName = filters.team === '__none__'
      ? 'Unassigned'
      : ((teams || []).find((t) => t.id === filters.team)?.name || 'Unknown team');
    chips.push({ key: 'team', label: `Team: ${teamName}`, clear: () => setFilter({ team: ALL }) });
  }
  if (filters.dateFrom) chips.push({ key: 'dateFrom', label: `From: ${filters.dateFrom}`, clear: () => setFilter({ dateFrom: '' }) });
  if (filters.dateTo) chips.push({ key: 'dateTo', label: `To: ${filters.dateTo}`, clear: () => setFilter({ dateTo: '' }) });
  if (filters.amountMin !== '') chips.push({ key: 'amountMin', label: `Min: ${cs()}${filters.amountMin}`, clear: () => setFilter({ amountMin: '' }) });
  if (filters.amountMax !== '') chips.push({ key: 'amountMax', label: `Max: ${cs()}${filters.amountMax}`, clear: () => setFilter({ amountMax: '' }) });
  if (filters.search) chips.push({ key: 'search', label: `Search: "${filters.search}"`, clear: () => setFilter({ search: '' }) });

  // ------------- CSV export -------------
  const handleExportCsv = () => {
    const headers = ['Date', 'Category', 'Age', 'Team', 'Description', 'Amount'];
    const lines = [headers.join(',')];
    for (const e of sorted) {
      const team = e.team_id ? ((teams || []).find((t) => t.id === e.team_id)?.name || '') : '';
      lines.push([
        csvEscape(e.expense_date || ''),
        csvEscape(displayCategoryName(e.category, expenseCategories)),
        csvEscape(e.age_category ? e.age_category.toUpperCase() : 'General'),
        csvEscape(team),
        csvEscape(e.description || ''),
        csvEscape((e.amount ?? 0).toFixed(2)),
      ].join(','));
    }
    lines.push(['', '', '', '', 'TOTAL', csvEscape(total.toFixed(2))].join(','));

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const slug = (tournament?.name || 'tournament').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    const today = new Date().toISOString().slice(0, 10);
    a.download = `${slug}-expenses-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center flex-wrap gap-2">
          <CardTitle>Expenses</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={handleExportCsv}
              disabled={sorted.length === 0}
              data-testid="expenses-export-csv-btn"
            >
              <Download className="h-4 w-4 mr-1" />
              Export CSV
            </Button>
            <Button size="sm" onClick={onAddExpense} data-testid="add-expense-btn">
              <Plus className="h-4 w-4 mr-1" />
              Add Expense
            </Button>
            {isAdmin && (
              <Button size="sm" variant="outline" onClick={onManageCategories} data-testid="manage-categories-btn">
                <Settings className="h-4 w-4 mr-1" />
                Categories
              </Button>
            )}
          </div>
        </div>

        {/* Filter bar */}
        <div className="mt-3 flex flex-wrap items-end gap-2 pt-3 border-t">
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-slate-500 uppercase tracking-wide">Category</Label>
            <Select value={filters.category} onValueChange={(v) => setFilter({ category: v })}>
              <SelectTrigger className="w-40 h-9" data-testid="expenses-filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All categories</SelectItem>
                {activeCategories.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.name}{!c.is_system ? ' · custom' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-slate-500 uppercase tracking-wide">Age</Label>
            <Select value={filters.age} onValueChange={(v) => setFilter({ age: v })}>
              <SelectTrigger className="w-32 h-9" data-testid="expenses-filter-age">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All ages</SelectItem>
                <SelectItem value="general">General</SelectItem>
                {(tournament?.age_categories || []).map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat.toUpperCase()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-slate-500 uppercase tracking-wide">Team</Label>
            <Select value={filters.team} onValueChange={(v) => setFilter({ team: v })}>
              <SelectTrigger className="w-40 h-9" data-testid="expenses-filter-team">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All teams</SelectItem>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {(teams || []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-slate-500 uppercase tracking-wide">From</Label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilter({ dateFrom: e.target.value })}
              className="w-36 h-9"
              data-testid="expenses-filter-date-from"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-slate-500 uppercase tracking-wide">To</Label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilter({ dateTo: e.target.value })}
              className="w-36 h-9"
              data-testid="expenses-filter-date-to"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-slate-500 uppercase tracking-wide">Min $</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0"
              value={filters.amountMin}
              onChange={(e) => setFilter({ amountMin: e.target.value })}
              className="w-24 h-9"
              data-testid="expenses-filter-amount-min"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-slate-500 uppercase tracking-wide">Max $</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="∞"
              value={filters.amountMax}
              onChange={(e) => setFilter({ amountMax: e.target.value })}
              className="w-24 h-9"
              data-testid="expenses-filter-amount-max"
            />
          </div>

          <div className="flex flex-col gap-1 grow min-w-[180px]">
            <Label className="text-[11px] text-slate-500 uppercase tracking-wide">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Description or category…"
                value={filters.search}
                onChange={(e) => setFilter({ search: e.target.value })}
                className="pl-7 h-9"
                data-testid="expenses-filter-search"
              />
            </div>
          </div>
        </div>

        {/* Active filter chips */}
        {chips.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">Active filters:</span>
            {chips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.clear}
                className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs px-2 py-0.5 hover:bg-blue-100 transition-colors"
                data-testid={`expenses-filter-chip-${chip.key}`}
              >
                {chip.label}
                <X className="h-3 w-3" />
              </button>
            ))}
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs text-slate-500 underline hover:text-slate-700"
              data-testid="expenses-filter-clear-all"
            >
              Clear all
            </button>
          </div>
        )}
      </CardHeader>

      <CardContent>
        {/* iter331b — Result count + filtered total always render so
            zero-match filter states still tell admins "Showing 0 of N"
            and "$0.00". Previously nested under the non-empty branch. */}
        {(expenses || []).length > 0 && (
          <div className="mb-2 text-xs text-slate-500 flex justify-between">
            <span data-testid="expenses-result-count">
              Showing {sorted.length} of {(expenses || []).length} expense{(expenses || []).length === 1 ? '' : 's'}
            </span>
            <span className="font-medium text-slate-700" data-testid="expenses-filtered-total">
              Total: {cs()}{total.toFixed(2)}
            </span>
          </div>
        )}
        {sorted.length === 0 ? (
          <div className="text-center py-8 text-slate-500" data-testid="expenses-empty">
            {(expenses || []).length === 0
              ? 'No expenses recorded'
              : 'No expenses match the current filters'}
          </div>
        ) : (
          <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader label="Date" sortKey="expense_date" sort={sort} setSort={setSort} testId="expenses-sort-date" />
                  <SortableHeader label="Category" sortKey="category" sort={sort} setSort={setSort} testId="expenses-sort-category" />
                  <SortableHeader label="Age Group" sortKey="age_category" sort={sort} setSort={setSort} testId="expenses-sort-age" />
                  <SortableHeader label="Team" sortKey="team" sort={sort} setSort={setSort} testId="expenses-sort-team" />
                  <SortableHeader label="Description" sortKey="description" sort={sort} setSort={setSort} testId="expenses-sort-description" />
                  <SortableHeader label="Amount" sortKey="amount" sort={sort} setSort={setSort} className="text-right" testId="expenses-sort-amount" />
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((expense) => {
                  const team = expense.team_id ? (teams || []).find((t) => t.id === expense.team_id) : null;
                  return (
                    <TableRow key={expense.id} data-testid={`expense-row-${expense.id}`}>
                      <TableCell className="text-slate-600">{expense.expense_date || '-'}</TableCell>
                      <TableCell className="font-medium">
                        {displayCategoryName(expense.category, expenseCategories)}
                      </TableCell>
                      <TableCell>
                        {expense.age_category
                          ? <Badge variant="outline">{expense.age_category.toUpperCase()}</Badge>
                          : <Badge variant="secondary">General</Badge>}
                      </TableCell>
                      <TableCell>
                        {team
                          ? <span className="text-sm text-slate-700">{team.name}</span>
                          : <span className="text-slate-400">-</span>}
                      </TableCell>
                      <TableCell className="text-slate-600">{expense.description || '-'}</TableCell>
                      <TableCell className="text-right font-medium">{cs()}{(expense.amount || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => onEditExpense(expense)} className="text-blue-600" data-testid={`edit-expense-${expense.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => onDeleteExpense(expense.id)} className="text-red-600" data-testid={`delete-expense-${expense.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
        )}
      </CardContent>
    </Card>
  );
};
